/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { usePrefixAchievements } from 'pages/Dashboard/components/prefix-achievements';
import { usePrefixRecognitions } from 'pages/Dashboard/components/recent-recognitions';
import { getUserByPrefixo } from 'services/dashboard.service';

import FeedHero from './components/FeedHero';
import { FOCO_VISIVEL } from 'utils/foco';
import FuncisParticipantes, { Funci, ID_DO_TITULO_DOS_FUNCIS } from './components/FuncisParticipantes';
import PerfilDoColega from './components/PerfilDoColega';
import PilulaDoCiclo from './components/PilulaDoCiclo';
import UltimasRealizacoes from './components/UltimasRealizacoes';
import UltimosReconhecimentos from './components/UltimosReconhecimentos';
import { POR_PAGINA } from './feed-calculos';

interface Selecionado {
  matricula: string;
  posicao: number;
}

/**
 * Cada navegação para o Feed, inclusive o clique no menu estando num perfil, abre a página inicial do módulo: a lista,
 * sem perfil aberto, na página 1 e sem busca. O perfil é estado da página, e não rota; por isso a página remonta a
 * cada chave de navegação.
 */
export default function Rede() {
  const { key } = useLocation();
  const primeiraChave = useRef(key);

  // Só rola ao voltar pelo menu: a primeira carga não mexe na rolagem (AJH-40).
  useEffect(() => {
    if (key !== primeiraChave.current) window.scrollTo(0, 0);
  }, [key]);

  return <FeedJogaJunto key={key} />;
}

function FeedJogaJunto() {
  const { user, userInfo, cycleSelected } = useAuth();
  const rede = !!user?.extra?.rede;
  const prefixo = userInfo?.prefixo;

  const { items: reconhecimentos } = usePrefixRecognitions(prefixo);
  const realizacoes = usePrefixAchievements(rede ? undefined : prefixo, cycleSelected?._id);

  const [termo, setTermo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [funcis, setFuncis] = useState<Funci[]>([]);
  const [total, setTotal] = useState<number>();
  const [participantes, setParticipantes] = useState<number>();
  const [carregando, setCarregando] = useState(true);
  const [selecionado, setSelecionado] = useState<Selecionado>();
  const tituloRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!user?._id || !prefixo) return;
    let ativo = true;

    setCarregando(true);
    getUserByPrefixo(prefixo, termo, pagina * POR_PAGINA, POR_PAGINA)
      .then(({ result, range }) => {
        if (!ativo) return;
        setFuncis(Array.isArray(result) ? result : []);
        setTotal(Number.isFinite(range.total) ? range.total : 0);
        // Busca vazia devolve a rede inteira: é o total de funcis participando (REDE-03).
        if (!termo && Number.isFinite(range.total)) setParticipantes(range.total);
      })
      .catch(() => {
        if (!ativo) return;
        setFuncis([]);
        setTotal(0);
      })
      .finally(() => ativo && setCarregando(false));

    return () => {
      ativo = false;
    };
  }, [user?._id, prefixo, termo, pagina]);

  // A página rola a janela (o AppShell não tem contêiner com overflow): sem isso, a troca de página
  // e a nova busca deixavam a tela no rodapé da grade, e o perfil abria rolado até o fim (AJH-34 a AJH-37).
  function rolarParaOsFuncis() {
    document.getElementById(ID_DO_TITULO_DOS_FUNCIS)?.scrollIntoView({ block: 'start' });
  }

  // A busca filtra enquanto se digita (AJ2-46): rolar a cada pausa faria a página pular sob o campo. Só rola
  // quando o título ficou acima da tela, que é o caso que a AJH-37 corrige.
  function rolarSeOTituloSumiu() {
    const titulo = document.getElementById(ID_DO_TITULO_DOS_FUNCIS);
    if (titulo && titulo.getBoundingClientRect().top < 0) rolarParaOsFuncis();
  }

  function buscar(novoTermo: string) {
    setTermo(novoTermo);
    setPagina(0);
    rolarSeOTituloSumiu();
  }

  function trocarPagina(novaPagina: number) {
    setPagina(novaPagina);
    rolarParaOsFuncis();
  }

  function abrirPerfil(funci: Funci) {
    setSelecionado({
      matricula: funci.matricula,
      posicao: pagina * POR_PAGINA + funcis.findIndex(item => item.matricula === funci.matricula),
    });
    window.scrollTo(0, 0);
  }

  function voltarParaLista() {
    setSelecionado(undefined);
    requestAnimationFrame(() => {
      tituloRef.current?.focus();
      window.scrollTo(0, 0);
    });
  }

  return (
    <>
      <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
        {selecionado ? (
          <PerfilDoColega matricula={selecionado.matricula} posicao={selecionado.posicao} onVoltar={voltarParaLista} />
        ) : (
          <div>
            <div className="mb-[22px] flex items-center gap-3.5">
              <Link
                to="/"
                aria-label="Voltar"
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-blue ${FOCO_VISIVEL}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--brand-yellow)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </Link>
              <h1 ref={tituloRef} tabIndex={0} className={`m-0 font-display text-[28px] font-extrabold leading-[normal] text-brand-blue-dark ${FOCO_VISIVEL}`}>
                Feed Joga Junto
              </h1>
              {cycleSelected?.title && <PilulaDoCiclo titulo={cycleSelected.title} className="ml-auto" />}
            </div>

            <FeedHero rede={rede} participantes={participantes} />

            <UltimosReconhecimentos prefixo={prefixo} itens={reconhecimentos} />

            {!rede && <UltimasRealizacoes prefixo={prefixo} itens={realizacoes} />}

            <FuncisParticipantes
              funcis={funcis}
              total={total}
              pagina={pagina}
              carregando={carregando}
              buscaInicial={termo}
              onBuscar={buscar}
              onPagina={trocarPagina}
              onAbrir={abrirPerfil}
            />
          </div>
        )}
      </div>

      <Outlet />
    </>
  );
}
